import { Grid, Step as MuiStep, StepLabel, Stepper } from '@material-ui/core'
import { Button } from 'components/facets'
import { useUser } from 'components/providers/UserProvider'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { IdGame, PriceStategyTarget, PricingStrategy, PRICING_BOT_SOURCE } from 'shared'
import { OptionBox } from '../OptionBox'
import { ExistingStrategiesPresenter } from './ExistingStrategiesPresenter'
import { FinalizePresenter } from './FinalizePresenter'

export interface MinMaxFlowPresenterProps {
  newName: string
  onRestart: () => void
  onDone: (strategy: PricingStrategy) => void
  dataTestid?: string
}

enum Step {
  Base = 1,
  Strategy1,
  Strategy2,
  Finalize,
}

const PLACEHOLDER = '—'

const StrategyPreview = ({
  strategy,
  strategy1,
  strategy2,
  step,
  progress,
}: {
  strategy: PricingStrategy
  strategy1: PricingStrategy | undefined
  strategy2: PricingStrategy | undefined
  step: Step
  progress: Step
}): JSX.Element => {
  const parameterTouched = progress > Step.Base
  const strategy1Touched = progress > Step.Strategy1
  const strategy2Touched = progress > Step.Strategy2
  const nameTouched = progress >= Step.Finalize

  const fieldClass = (touched: boolean, isActive: boolean): string => {
    const classes = ['wizard-preview__field']
    if (!touched) classes.push('wizard-preview__field--untouched')
    if (isActive) classes.push('wizard-preview__field--active')
    return classes.join(' ')
  }

  const parameterLabel = parameterTouched ? String(strategy.base.tactic.parameter) : PLACEHOLDER
  const strategy1Label = strategy1Touched && strategy1 ? strategy1.name : PLACEHOLDER
  const strategy2Label = strategy2Touched && strategy2 ? strategy2.name : PLACEHOLDER

  return (
    <div>
      <h6
        className={`wizard-preview__title ${
          step === Step.Finalize ? 'wizard-preview__field--active' : ''
        }`}
      >
        {nameTouched ? strategy.name || PLACEHOLDER : PLACEHOLDER}
      </h6>
      <p className="wizard-preview__sentence">
        <span className={fieldClass(parameterTouched, step === Step.Base)}>{parameterLabel}</span>
        {step >= Step.Strategy1 && (
          <>
            {' '}
            between{' '}
            <span className={fieldClass(strategy1Touched, step === Step.Strategy1)}>
              {strategy1Label}
            </span>
          </>
        )}
        {step >= Step.Strategy2 && (
          <>
            {' '}
            and{' '}
            <span className={fieldClass(strategy2Touched, step === Step.Strategy2)}>
              {strategy2Label}
            </span>
          </>
        )}
      </p>
    </div>
  )
}

export const MinMaxFlowPresenter = ({
  newName,
  onRestart,
  onDone,
  dataTestid,
}: MinMaxFlowPresenterProps): JSX.Element => {
  const { t } = useTranslation()
  const { user } = useUser()

  const [step, setStep] = useState<Step>(Step.Base)
  const [progress, setProgress] = useState<Step>(Step.Base)
  // Clicking a box only selects it; the user advances with the always-visible
  // Next button, which stays disabled until a real choice is made.
  const [parameterSelected, setParameterSelected] = useState(false)

  const minMaxStrategyIds: string[] = []
  user.pricingStrategies.forEach((strategy) => {
    if (strategy.base.kind === 'minMax') {
      minMaxStrategyIds.push(strategy._id)
    }
  })

  const [strategy, setStrategy] = useState<PricingStrategy>({
    _id: '2',
    name: newName,
    base: {
      kind: 'minMax',
      tactic: { parameter: 'Min' },
    },
    modifier: {
      kind: 'percent',
      value: 95,
    },
    bulkSettingsId: {
      [IdGame.Magic]: '',
      [IdGame.Yugioh]: '',
      [IdGame.Pokemon]: '',
      [IdGame.Vanguard]: '',
      [IdGame.DBS]: '',
      [IdGame.FF]: '',
      [IdGame.WS]: '',
      [IdGame.FAB]: '',
      [IdGame.Digimon]: '',
    },
    source: PRICING_BOT_SOURCE.CARDMARKET,
    target: PriceStategyTarget.sellPrice,
  })

  const [chosenStrategy1, setChosenStrategy1] = useState<PricingStrategy | undefined>(undefined)
  const [chosenStrategy2, setChosenStrategy2] = useState<PricingStrategy | undefined>(undefined)

  const stepLabels = [
    t('strategy.parameter'),
    t('strategy.strategy1'),
    t('strategy.strategy2'),
    t('strategy.name'),
  ]

  const advance = (current: Step): void => {
    setStep(current + 1)
    setProgress(Math.max(current + 1, progress))
  }

  const stepBase = (): JSX.Element => (
    <>
      <h6 className="m-0 p-0 mb-2">{t('strategy.create.minMaxParameterDescription')}</h6>
      <p className="mb-3">{t('strategy.create.minMaxParameterDescription1')}</p>
      <Grid container>
        {['Min', 'Max'].map((param) => (
          <Grid xs={6} key={param} item data-testid={`box-${param}`}>
            <OptionBox
              selected={parameterSelected && strategy.base.tactic.parameter === param}
              onClick={() => {
                const strategyCopy = { ...strategy }
                strategyCopy.base.tactic.parameter = param
                setStrategy(strategyCopy)
                setParameterSelected(true)
              }}
              title={param}
              width="160px"
              dataTestid={dataTestid + `-${param}`}
            />
          </Grid>
        ))}
      </Grid>
    </>
  )

  const stepStrategy1 = (): JSX.Element => (
    <>
      <p className="mb-3">{t('strategy.create.minMaxStrategy1Description')}</p>
      <ExistingStrategiesPresenter
        hiddenStrategiesId={minMaxStrategyIds}
        selectedStrategyId={chosenStrategy1?._id}
        handleBoxClick={(chosen) => {
          setChosenStrategy1(chosen)
          const strategyCopy = { ...strategy }
          strategyCopy.base.tactic.strategy1 = chosen._id
          setStrategy(strategyCopy)
        }}
      />
    </>
  )

  const stepStrategy2 = (): JSX.Element => (
    <>
      <p className="mb-3">{t('strategy.create.minMaxStrategy2Description')}</p>
      <ExistingStrategiesPresenter
        hiddenStrategiesId={[chosenStrategy1?._id ?? '', ...minMaxStrategyIds]}
        selectedStrategyId={chosenStrategy2?._id}
        handleBoxClick={(chosen) => {
          setChosenStrategy2(chosen)
          const strategyCopy = { ...strategy }
          strategyCopy.base.tactic.strategy2 = chosen._id
          setStrategy(strategyCopy)
        }}
      />
    </>
  )

  const stepFinalize = (): JSX.Element => (
    <FinalizePresenter strategy={strategy} setStrategy={setStrategy} />
  )

  const fullWidthStep = false

  const nextConfig = ((): {
    onClick: () => void
    isLast?: boolean
    disabled?: boolean
  } | null => {
    switch (step) {
      case Step.Base:
        return {
          onClick: () => advance(Step.Base),
          disabled: !parameterSelected,
        }
      case Step.Strategy1:
        return {
          onClick: () => advance(Step.Strategy1),
          disabled: !chosenStrategy1,
        }
      case Step.Strategy2:
        return {
          onClick: () => advance(Step.Strategy2),
          disabled: !chosenStrategy2,
        }
      case Step.Finalize:
        return {
          onClick: () => onDone(strategy),
          isLast: true,
          disabled: strategy.name.trim() === '',
        }
      default:
        return null
    }
  })()

  return (
    <div className="p-3 mb-5">
      <button
        type="button"
        onClick={onRestart}
        className="wizard-back mb-2"
        data-testid="restart-strategy-btn"
      >
        ← {t('strategy.create.changeType')}
      </button>
      <Stepper activeStep={step - 1} alternativeLabel className="wizard-stepper">
        {stepLabels.map((label, index) => {
          const stepNumber = index + 1
          const canNavigate = progress >= stepNumber
          return (
            <MuiStep key={label}>
              <StepLabel
                style={{ cursor: canNavigate ? 'pointer' : 'default' }}
                onClick={() => canNavigate && setStep(stepNumber)}
                data-testid={`step-${index}`}
              >
                {label}
              </StepLabel>
            </MuiStep>
          )
        })}
      </Stepper>
      <Grid container spacing={4} className="h-100 mt-3">
        <Grid item xs={fullWidthStep ? 12 : 7}>
          <div className="pt-2">
            {step === Step.Base && stepBase()}
            {step === Step.Strategy1 && stepStrategy1()}
            {step === Step.Strategy2 && stepStrategy2()}
            {step === Step.Finalize && stepFinalize()}
          </div>
        </Grid>
        {!fullWidthStep && (
          <Grid item xs={5} className="d-flex flex-column justify-content-start mt-4 h-100">
            <div className="wizard-preview">
              <div className="wizard-preview__label">{t('competitor.create.previewLabel')}</div>
              <StrategyPreview
                strategy={strategy}
                strategy1={chosenStrategy1}
                strategy2={chosenStrategy2}
                step={step}
                progress={progress}
              />
            </div>
          </Grid>
        )}
      </Grid>
      {nextConfig && (
        <div className="d-flex justify-content-end mt-3">
          <Button
            disabled={nextConfig.disabled}
            onClick={nextConfig.onClick}
            variant="contained"
            data-testid="next-btn"
          >
            {t(`common.${nextConfig.isLast ? 'done' : 'next'}`)}
          </Button>
        </div>
      )}
    </div>
  )
}
